import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, MessageSquare } from 'lucide-react';
import MagneticButton from './MagneticButton';

// ─── Hero ─────────────────────────────────────────────────────────────────────
const fadeIn = (delay: number) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay, ease: [0.16, 1, 0.3, 1] as const },
});

const Hero = () => {
  const reduceMotion = useReducedMotion();
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (reduceMotion) return;
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
      el.style.setProperty('--my', `${e.clientY - rect.top}px`);
    };
    el.addEventListener('mousemove', onMove, { passive: true });
    return () => el.removeEventListener('mousemove', onMove);
  }, [reduceMotion]);

  return (
    <section
      ref={heroRef}
      className="min-h-screen flex items-center justify-center relative overflow-hidden pt-16 bg-[#060d18] [--mx:50%] [--my:50%]"
      id="home"
    >
      {/* Dot grid */}
      <div className="absolute inset-0 hero-grid opacity-100 pointer-events-none" />

      {/* Nebula Layer A — large ambient drift */}
      {!reduceMotion && (
        <motion.div
          className="absolute pointer-events-none"
          style={{
            width: '80vw', height: '50vh',
            top: '5%', left: '10vw',
            background: 'radial-gradient(ellipse at center, rgba(0,200,255,0.06) 0%, rgba(0,200,255,0.02) 40%, transparent 70%)',
            filter: 'blur(40px)',
          }}
          animate={{ x: ['0%', '5%', '-3%', '0%'], opacity: [0.6, 1, 0.7, 0.6] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      {/* Nebula Layer B — secondary concentrated glow */}
      {!reduceMotion && (
        <motion.div
          className="absolute pointer-events-none"
          style={{
            width: '40vw', height: '30vh',
            bottom: '15%', right: '5vw',
            background: 'radial-gradient(ellipse at center, rgba(0,200,255,0.07) 0%, transparent 65%)',
            filter: 'blur(60px)',
          }}
          animate={{ x: ['0%', '-8%', '4%', '0%'], y: ['0%', '6%', '-4%', '0%'], opacity: [0.5, 0.9, 0.6, 0.5] }}
          transition={{ duration: 24, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
        />
      )}

      {/* Static radial gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(0,200,255,0.08),transparent)] pointer-events-none" />

      {/* Cursor spotlight */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
        style={{
          background: 'radial-gradient(600px circle at var(--mx) var(--my), rgba(0,200,255,0.06), transparent 70%)',
        }}
      />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10 w-full">
        <div className="grid grid-cols-1 items-center">

          {/* Left column */}
          <div className="max-w-2xl space-y-6">
            <motion.div {...(reduceMotion ? {} : fadeIn(0.05))}>
              <h1
                className="font-sans font-bold text-white leading-[1.04] tracking-tight"
                style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', letterSpacing: '-0.03em' }}
              >
                Applied AI
              </h1>
            </motion.div>

            <motion.p
              className="text-base text-white/50 max-w-md leading-relaxed"
              {...(reduceMotion ? {} : fadeIn(0.18))}
            >
              I build production AI systems, spanning agentic orchestration, RAG,
              evaluations, guardrails, infrastructure, and cloud deployment.
            </motion.p>

            {/* CTA buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-3"
              {...(reduceMotion ? {} : fadeIn(0.26))}
            >
              <MagneticButton strength={0.3}>
                <a
                  href="#projects"
                  className="relative overflow-hidden btn-shimmer inline-flex items-center justify-center gap-2 bg-[#00c8ff] hover:bg-[#33d6ff] text-[#060d18] font-semibold px-6 py-3 rounded-md transition-colors duration-200 text-sm"
                >
                  View Projects
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center gap-2 border border-white/[0.12] hover:border-[#00c8ff]/40 text-white/70 hover:text-white px-6 py-3 rounded-md transition-colors duration-200 text-sm"
                >
                  <MessageSquare className="w-4 h-4" />
                  Say Hello
                </a>
              </MagneticButton>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
